import { type ReactNode } from 'react';
import { Box, Flex, Loader, Stack, Switch, Text, UnstyledButton } from '@mantine/core';
import { IconCheck, IconRefresh } from '@tabler/icons-react';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { splitMarkdownSections } from './splitMarkdownSections';

type Props = {
  title: string;
  markdownContent: string;
  onRegenerate?: () => void;
  isRegenerating?: boolean;
  summarise?: boolean;
  onSummariseChange?: (value: boolean) => void;
  isReviewed?: boolean;
  onToggleReviewed?: (isReviewed: boolean) => void;
  isSettingReviewed?: boolean;
};

export function ReviewableMeetingNote({
  title,
  markdownContent,
  onRegenerate,
  isRegenerating = false,
  summarise = true,
  onSummariseChange,
  isReviewed = false,
  onToggleReviewed,
  isSettingReviewed = false,
}: Props) {
  const sections = splitMarkdownSections(markdownContent);

  return (
    <Box bg={isReviewed ? 'success.1' : 'warning.1'} p="sm" style={{ borderRadius: 12 }}>
      <Flex justify="space-between" align="center" gap="sm">
        <Flex align="center" gap="xxs" style={{ minWidth: 0 }}>
          <Box
            w={12}
            h={12}
            bg={isReviewed ? 'success.5' : 'warning.5'}
            style={{ borderRadius: '50%', flexShrink: 0 }}
          />
          <Text size="lg" fw={FONT_WEIGHTS.semibold} c="gray.10">
            {title} {isReviewed ? '(reviewed)' : '(not reviewed)'}
          </Text>
        </Flex>
        <Flex align="center" gap="md" style={{ flexShrink: 0 }}>
          <Flex align="center" gap="xxs">
            <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
              Summarise
            </Text>
            <Switch
              checked={summarise}
              onChange={event => onSummariseChange?.(event.currentTarget.checked)}
              disabled={isRegenerating || isReviewed}
            />
          </Flex>
          <HeaderAction
            icon={
              isRegenerating ? (
                <Loader size={16} color="var(--mantine-color-brand-6)" />
              ) : (
                <IconRefresh size={16} color="var(--mantine-color-brand-6)" />
              )
            }
            onClick={onRegenerate}
            disabled={!onRegenerate || isRegenerating || isReviewed}
          >
            Regenerate
          </HeaderAction>
          <HeaderAction
            icon={
              isSettingReviewed ? (
                <Loader size={16} color="var(--mantine-color-brand-6)" />
              ) : (
                <IconCheck size={16} color="var(--mantine-color-brand-6)" />
              )
            }
            onClick={onToggleReviewed ? () => onToggleReviewed(!isReviewed) : undefined}
            disabled={!onToggleReviewed || isSettingReviewed}
          >
            {isReviewed ? 'Mark as not reviewed' : 'Mark as reviewed'}
          </HeaderAction>
        </Flex>
      </Flex>

      <Stack mt="sm" gap="xxxs">
        {sections.map((section, index) => (
          <Box
            key={`${index}-${section.slice(0, 40)}`}
            bg="white"
            px="xs"
            py="xxs"
            style={{ borderRadius: 12 }}
          >
            <MarkdownEditor
              defaultValue={section}
              readonly
              withBorder={false}
              bg="white"
              rootClass="compact"
              showMarkers={false}
              style={{ borderWidth: 0 }}
              px="xs"
              py={0}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function HeaderAction({
  icon,
  children,
  onClick,
  disabled = false,
}: {
  icon: ReactNode;
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      disabled={disabled}
      style={{ cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1 }}
    >
      <Flex align="center" gap="xxxs">
        {icon}
        <Text size="sm" fw={FONT_WEIGHTS.semibold} c="brand.6">
          {children}
        </Text>
      </Flex>
    </UnstyledButton>
  );
}
